import {
  Component,
  OnInit,
  QueryList,
  ViewChildren,
  ChangeDetectorRef,
  ViewChild,
  Input,
  HostBinding,
  inject,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router, ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, forkJoin, Observable, of } from 'rxjs';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { HttpResponse } from '@angular/common/http';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FiltersFactoryService } from 'src/app/services/filters-factory.service';
import { changeFiltersToAPIFormat, FiltersService } from 'src/app/services/filters.service';
import { FilterData } from 'src/app/standalones/components/generic-filters-view/filters.interface';
import { UiService } from 'src/app/services/ui.service';
import { NotificationToasterService } from 'src/app/services/notification-toaster.service';
import { modifiedSetTimeout } from 'src/app/utilities/ZoneUtils';
import { skip } from 'rxjs/operators';
import { ResourcesService } from '../../services/resources.service';
import { UploadDialogComponent } from '../../dialogs/media-upload/upload-dialog.component';
import { FileDetails, UploadTaskComponent } from '../upload-task/upload-task.component';
import {
  SUPPORTED_UPLOAD_ACCEPT_TYPES,
  UploadFileService,
} from '../../services/upload-file.service';
import { ResourceFilterParamsEnum } from '../../models/params';
import {
  IResource,
  ResourceItemModel,
  IFolder,
  IFolderResponse,
  IDisplayFile,
  IDisplayFolder,
} from '../../models/resource';
import { MoveToFolderComponent } from '../../dialogs/move-to-folder/move-to-folder.component';
import { SnackbarComponent } from '../../ui/snackbar.component';
import { AclService, Feature } from '../../services/acl.service';
import { User, Course } from '../../models/user';
import { UserService } from '../../services/user.service';
import { CreateFolderDialogComponent } from '../../dialogs/create-folder-dialog/create-folder-dialog.component';
import { IconTypes } from '../../standalones/components/pencil-icon/pencil-icon.component';
import { FileViewComponent } from './file-view/base-file-view.component';

export enum animationStates {
  VISIBLE = 'visible',
  HIDDEN = 'hidden',
}

export interface IActiveFolder extends IFolderResponse {
  resources: {
    files: IDisplayFile[] | IResource[];
    page: number;
    pages: number;
    total: number;
  };
}

type FolderBreadcrumbItem = Pick<IFolder, '_id' | 'name'>;

@UntilDestroy()
@Component({
  selector: 'app-files',
  templateUrl: './files.component.html',
  styleUrls: ['./files.component.scss'],
  providers: [FiltersService],
})
export class FilesComponent implements OnInit {
  private readonly uiService = inject(UiService);
  private readonly notificationToasterService = inject(NotificationToasterService);
  private readonly aclService = inject(AclService);

  // Selected course syllabus.
  @Input() selectedCourse?: Course;

  @Input() enforcingFilter?: ResourceFilterParamsEnum;

  @Input() user?: User;

  @HostBinding('class') classes = this.userService.rtl.getValue()
    ? 'border-start flex-row-reverse'
    : 'border-end';

  isRtl$ = this.userService.rtl.asObservable();

  filterPanelOpen = false;
  filesLoading$ = new BehaviorSubject<boolean>(true);
  scrolling = false;
  filePage = 1;
  folderPage = 1;
  allSelected = false;
  foldersPerPage = 1000; // show all folders
  filesPerPage = 25;
  isUploading = false;
  uploadFiles: File[] = [];
  finalizedFiles = 0;
  actionText = this.translate.instant('Uploading...');
  uploadPanelRef: ReturnType<typeof setTimeout> | null = null;
  // opened folder in list view
  activeFolder?: IActiveFolder;
  @ViewChildren('uploadTask') UploadTasks!: QueryList<UploadTaskComponent>;
  maxNameLength = 40;
  views = {
    LIST: 'LIST',
    GRID: 'GRID',
  };
  view: string;
  folderCache: Record<string, Record<string, IFolderResponse>> = {};
  rootId = '';
  rootFolderId = '';
  institutionId = '';
  Features = Feature;
  readonly IconTypes = IconTypes;
  readonly supportedUploadTypes = SUPPORTED_UPLOAD_ACCEPT_TYPES;
  @ViewChild('filesList') fileList!: FileViewComponent;

  // Enum of resource types For html templates.
  resourceTypes = ResourceItemModel;

  constructor(
    public resourceService: ResourcesService,
    private router: Router,
    public dialog: MatDialog,
    public translate: TranslateService,
    public userService: UserService,
    private snackBar: MatSnackBar,
    private activatedRoute: ActivatedRoute,
    private changeDetection: ChangeDetectorRef,
    public filtersService: FiltersService,
    private filtersFactoryService: FiltersFactoryService,
  ) {
    this.view = this.views.LIST;

    this.resourceService.createResourceIntent.pipe(untilDestroyed(this)).subscribe((show) => {
      if (show) {
        this.showUploadDialog();
      }
    });

    this.resourceService.createFolderIntent.pipe(untilDestroyed(this)).subscribe(() => {
      this.createFolderDialog();
    });
  }

  // Use this for selections
  get currentFiles(): IDisplayFile[] {
    return (this.activeFolder?.resources.files ?? []) as IDisplayFile[];
  }

  get selectedFiles(): IDisplayFile[] {
    return this.fileList?.selectedFilesObj ?? [];
  }

  // Use this for selections
  get currentFolders(): IDisplayFolder[] {
    if (this.hideFoldersForCurrentView) {
      return [];
    }
    return (this.activeFolder?.subdirectories ?? []) as IDisplayFolder[];
  }

  get displayedResultsCount(): number {
    return (
      (this.activeFolder?.resources.total ?? 0) +
      (this.hideFoldersForCurrentView ? 0 : this.activeFolder?.total ?? 0)
    );
  }

  get breadcrumbParent(): FolderBreadcrumbItem | undefined {
    if (!this.activeFolder || this.isRootFolder(this.activeFolder)) {
      return undefined;
    }

    return this.getParentFolderForBreadcrumb(this.activeFolder);
  }

  get selectedFolders(): IDisplayFolder[] {
    return this.fileList?.selectedFoldersObj ?? [];
  }

  get hasSelection(): boolean {
    return this.selectedFiles.length > 0 || this.selectedFolders.length > 0;
  }

  get isFiltersApplied(): boolean {
    return this.filtersService.appliedFilters.length + this.filtersService.searchValue.length > 0;
  }

  get canUploadFiles(): boolean {
    return !!this.user && this.aclService.isAllowed(this.user, Feature.upload_resource);
  }

  get hideFoldersForCurrentView(): boolean {
    return this.filtersService.appliedFilters.length > 0;
  }

  get activeFolderIsRoot(): boolean {
    return this.isRootFolder(this.activeFolder);
  }

  private shouldSwitchView(view: string): boolean {
    return Object.values(this.views).includes(view) && view !== this.view;
  }

  ngOnInit(): void {
    this.initializeFilesFilters();
    this.uiService.setTabTitle('Files');
    this.activatedRoute.queryParams.pipe(untilDestroyed(this)).subscribe((queryParams) => {
      if (this.shouldSwitchView(queryParams.view)) {
        this.switchView();
      }
    });
    this.activatedRoute.params.pipe(untilDestroyed(this)).subscribe((params) => {
      const requestedFolderId = params.folderId || 'root';
      this.rootId = requestedFolderId;
      this.filePage = 1;
      this.folderPage = 1;
      const filter = this.buildFilters();
      this.getFolder(requestedFolderId, filter).then((folder) => {
        if (!folder) {
          return;
        }
        this.appendFilesAndFolders(folder);
        this.updateFolderContext(folder, requestedFolderId);
      });
    });

    if (!this.user) {
      this.userService.user.pipe(untilDestroyed(this)).subscribe((res) => (this.user = res?.user));
    }
  }

  protected initializeFilesFilters(): void {
    this.filtersFactoryService
      .createFilesFilters()
      .pipe(untilDestroyed(this))
      .subscribe((filters: FilterData[]) => {
        this.filtersService.initFilters(filters);
        this.subscribeToFilterChanges();
      });
  }

  protected subscribeToFilterChanges(): void {
    this.filtersService.appliedFiltersWithSingleEmpty$
      .pipe(skip(1), untilDestroyed(this))
      .subscribe(() => {
        this.resetView();
      });
  }

  protected buildFilters(): Record<string, any> {
    return (
      changeFiltersToAPIFormat(
        this.filtersService.appliedFilters,
        this.filtersService.searchValue,
      ) ?? {}
    );
  }

  protected updateFolderContext(folder: IFolderResponse, requestedFolderId: string): void {
    this.institutionId = requestedFolderId === 'root' ? folder.name : folder.institution;

    if (requestedFolderId === 'root') {
      this.rootFolderId = folder._id;
      this.rootId = folder._id;
      return;
    }

    if (folder.path?.length) {
      this.rootFolderId = folder.path[0];
      return;
    }

    const parentFolder = this.getParentFolderForBreadcrumb(folder);
    if (parentFolder?.name === this.institutionId) {
      this.rootFolderId = parentFolder._id;
    }
  }

  private getParentFolderForBreadcrumb(folder: IFolderResponse): FolderBreadcrumbItem | undefined {
    const parentFolder = folder.parentFolder as string | FolderBreadcrumbItem | undefined;
    if (!parentFolder) {
      return undefined;
    }

    if (typeof parentFolder === 'object') {
      return parentFolder;
    }

    if (
      parentFolder === this.rootFolderId ||
      folder.path?.length === 1 ||
      folder.path?.[0] === parentFolder
    ) {
      // The API only returns the root parent as an id in these cases: direct children
      // have a single-item path, and scoped folder responses store the institution root at path[0].
      return { _id: parentFolder, name: this.institutionId };
    }

    return undefined;
  }

  async getFolder(
    folderId: string | undefined,
    filters: Record<string, any> = {},
  ): Promise<IFolderResponse | undefined> {
    if (folderId) {
      // check 'cache' if filters haven't changed
      // TODO: find a more suitable way such as hashing.
      const cachedFolder = this.folderCache[folderId]?.[JSON.stringify(filters)];
      if (cachedFolder?.page >= this.folderPage && cachedFolder?.resources.page >= this.filePage) {
        return cachedFolder;
      } else {
        this.filesLoading$.next(true);
        const folder = await this.resourceService
          .getFolder(folderId, {
            folder_num: this.foldersPerPage,
            folder_page: this.folderPage,
            resource_num: this.filesPerPage,
            resource_page: this.filePage,
            filters,
          })
          .toPromise();
        if (folder) {
          this.saveFolder(folder, filters);
        }
        this.filesLoading$.next(false);
        return folder;
      }
    }
  }

  appendFilesAndFolders(folder: IFolderResponse, replace = false): void {
    if (folder) {
      if (replace || folder._id !== this.activeFolder?._id) {
        this.activeFolder = folder;
      } else if (this.activeFolder?.page < folder.page) {
        this.activeFolder.page = folder.page;
        this.fileList.addFiles(folder.resources.files, -1);
      }
      // if not last page
      if (this.filePage <= folder.resources.page) {
        this.filePage++;
      }

      // if not last page
      if (this.folderPage <= folder.page) {
        this.folderPage++;
      }
    }
  }

  updateCacheFor(
    folderId: string,
    newResources: IDisplayFile[],
    newFolders: IDisplayFolder[],
  ): void {
    const emptyFilter = JSON.stringify({});
    // For now, We will need to invalidate cache for this folder, but we can keep the original list(without filters).
    const cachedFolder = this.folderCache[folderId]?.[emptyFilter];
    // Invalidating
    this.folderCache[folderId] = {};
    if (cachedFolder) {
      cachedFolder.resources.files.unshift(...newResources);
      cachedFolder.subdirectories.unshift(...newFolders);
      cachedFolder.total += newFolders.length;
      cachedFolder.resources.total += newResources.length;
    }
    this.folderCache[folderId][emptyFilter] = cachedFolder;
  }

  saveFolder(folder: IFolderResponse, filters: Record<string, any>): void {
    const stringifiedFilters = JSON.stringify(filters);
    const cachedFolder = this.folderCache[folder._id]?.[stringifiedFilters];
    if (cachedFolder) {
      cachedFolder.resources.page = folder.resources.page;
      cachedFolder.page = folder.page;
      if (this.activeFolder?._id === folder._id) {
        this.fileList.addFiles(folder.resources.files, -1);
        this.fileList.addFolders(folder.subdirectories, -1, false);
      } else {
        cachedFolder.resources.files = cachedFolder.resources.files.concat(folder.resources.files);
        cachedFolder.subdirectories = cachedFolder.subdirectories.concat(folder.subdirectories);
      }
    } else {
      if (!this.folderCache[folder._id]) {
        this.folderCache[folder._id] = {};
      }
      this.folderCache[folder._id][stringifiedFilters] = folder;
    }
  }

  saveResource(fileDetails: FileDetails): void {
    if (this.finalizedFiles === this.uploadFiles.length - 1) {
      this.actionText = this.translate.instant('Finalizing...');
    }
    this.finalizedFiles++;
    const courses = this.selectedCourse?._id ? [this.selectedCourse._id] : [];
    if (fileDetails.name && fileDetails.size && this.activeFolder?._id) {
      const tempIResource: Partial<IResource> = {
        url: fileDetails.url,
        name: fileDetails.name,
        size: fileDetails.size,
        acl: {
          visibility: 'CLASS',
        },
        type: ((fDetails) => UploadFileService.checkFileType(fDetails))(fileDetails),
        metadata: {},
        courses: courses,
        folder: this.activeFolder?._id,
      };

      this.resourceService
        .createResource(tempIResource as IResource)
        .pipe(untilDestroyed(this))
        .subscribe((res) => {
          // insert to view
          if (res?.body) {
            if (this.activeFolder) {
              this.fileList.addFiles([res.body.resource], 1, true);
            }
          }
          if (this.finalizedFiles === this.uploadFiles.length) {
            this.actionText = this.translate.instant('Finished upload');
            this.dismissUploadMonitor();
          }
        });
    }
  }

  createFolderDialog(): void {
    const dialogRef = this.dialog.open(CreateFolderDialogComponent, {
      // The SMALL modal size constrains its content, but MatDialog still needs an overlay width.
      width: '320px',
      data: {
        parent: this.activeFolder,
        title: this.translate.instant(this.hasSelection ? 'Add to new folder' : 'Create folder'),
      },
    });

    // When it's closed, we check if the user wants ACTUALLY to create one.
    // if Yes, then we Call createFolder procedure.
    dialogRef
      .afterClosed()
      .pipe(untilDestroyed(this))
      .subscribe((name) => {
        if (name) {
          this.createFolder(name);
        }
      });
  }

  createFolder(name: string): void {
    const parent = this.activeFolder?._id;
    const onSuccess = (res: HttpResponse<IFolder>) => {
      const body = <IFolder>res.body;
      if (this.activeFolder) {
        this.fileList.addFolders([body], 1, true, true);
      }

      this.changeDetection.detectChanges();
      this.notificationToasterService.showDefaultSuccessNotification(
        this.translate.instant('Folder successfully created'),
        this.notificationToasterService.generateNotificationCode(),
      );
    };

    const onFailure = () =>
      this.notificationToasterService.showDefaultErrorNotificationV2(
        this.translate.instant('Failed to create folder'),
        '',
        this.notificationToasterService.generateNotificationCode(),
      );

    this.resourceService
      .createFolder({
        name: name.trim(),
        acl: {
          visibility: 'CLASS',
          public: false,
        },
        parentFolder: parent,
      })
      .pipe(untilDestroyed(this))
      .subscribe(
        (created) => {
          if (created && created.body) {
            this.movingSelectedFolderAndFilesTo({
              _id: created.body._id,
              name: created.body.name,
            })
              .pipe(untilDestroyed(this))
              .subscribe(
                () => onSuccess(created),
                () => onFailure(),
              );
          }
        },
        () => onFailure(),
      );
  }

  showUploadDialog(): void {
    const uploadRef = this.dialog.open(UploadDialogComponent, {
      width: '556px',
      panelClass: 'file-upload-dialog',
      data: {
        title: this.translate.instant('Upload File'),
        type: [this.resourceTypes.ANY],
        forceLegacyUpload: true,
        acceptFiles: this.supportedUploadTypes,
      },
    });

    uploadRef
      .afterClosed()
      .pipe(untilDestroyed(this))
      .subscribe((files) => {
        if (files?.length) {
          this.startUpload(Array.from(files));
        }
        this.resourceService.createResourceIntent.next(false);
      });
  }

  handleFileSelection(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.startUpload(Array.from(input.files));
    }
    input.value = '';
  }

  handleFileDrop(files: FileList): void {
    this.startUpload(Array.from(files));
  }

  private startUpload(files: File[]): void {
    if (!files.length || !this.canUploadFiles) {
      return;
    }

    this.keepUploadMonitor();
    this.uploadFiles = this.uploadFiles.concat(files);
    this.isUploading = true;
    this.actionText = this.translate.instant('Uploading...');
  }

  showMoveToFolderDialog() {
    const uploadRef = this.dialog.open(MoveToFolderComponent, {
      width: '418px',
      height: '501px',
      panelClass: 'move-to-folder-dialog',
      position: {
        top: '145px',
        right: '27px',
      },
      data: {
        selectedFiles: this.selectedFiles,
        selectedFolders: this.selectedFolders,
        activeFolderID: this.activeFolder?._id,
      },
    });

    uploadRef
      .afterClosed()
      .pipe(untilDestroyed(this))
      .subscribe((data) => {
        // user cancel or closed the dialog, then do nothing.
        if (!data) {
          return;
        }

        if (data.selectedFolder._id === this.activeFolder?._id) {
          return this.notificationToasterService.showDefaultErrorNotificationV2(
            this.translate.instant(
              "You can't move your files to their current folder, Please Select another folder.",
            ),
            '',
            this.notificationToasterService.generateNotificationCode(),
          );
        }

        // Some feedback to show that his request is on progress.
        this.notificationToasterService.showDefaultInfoNotificationV2(
          this.translate.instant('Working on Moving your files.'),
          '',
          this.notificationToasterService.generateNotificationCode(),
          undefined,
          undefined,
          1,
        );

        this.movingSelectedFolderAndFilesTo(data.selectedFolder)
          .pipe(untilDestroyed(this))
          .subscribe((res) => {
            // patching folders doesn't return the edited folders.
            const folders = this.selectedFolders.map((f) => f._id);
            const files = (res.resources as any)?.success || [];
            if (this.selectedFiles.length && !files.length) {
              return this.notificationToasterService.showDefaultErrorNotificationV2(
                this.translate.instant('Something is wrong, Try again!'),
                '',
                this.notificationToasterService.generateNotificationCode(),
              );
            }
            // Reflecting Completion of moving on the screen.
            this.fileList.movingSelectionView(files, folders, data.selectedFolder);
          });
      });
  }

  movingSelectedFolderAndFilesTo(destFolder: {
    _id: string;
    name: string;
  }): Observable<{ resources: object | null; folders: object | null }> {
    const resources = this.selectedFiles.map((file) => ({ _id: file._id, folder: destFolder._id }));
    const folders = this.selectedFolders.map((folder) => ({
      _id: folder._id,
      parentFolder: destFolder._id,
    }));

    return forkJoin({
      resources: resources.length ? this.resourceService.editResources(resources) : of(null),
      folders: folders.length ? this.resourceService.editFolders(folders) : of(null),
    });
  }

  onScrollDown(): void {
    if (this.filesLoading$.getValue()) {
      return;
    }
    this.scrolling = true;
    this.getFolder(this.activeFolder?._id as string, this.buildFilters()).then((folder) => {
      if (folder) {
        this.appendFilesAndFolders(folder);
      }
      this.scrolling = false;
    });
  }

  setSearchValue(searchVal: string): void {
    const searchValue = searchVal.trim();
    if (this.filtersService.searchValue !== searchValue) {
      this.filtersService.setSearchValue(searchValue);
      this.resetView();
    }
  }

  resetView(): void {
    this.toggleAll(false);
    this.filePage = 1;
    this.folderPage = 1;
    const folderId = this.activeFolder?._id as string;
    const filters = this.buildFilters();
    this.getFolder(folderId, filters).then((folder) => {
      if (folder) {
        this.appendFilesAndFolders(folder, true);
      }
    });
  }

  openFolder(folder: string | IFolderResponse): void {
    this.toggleAll(false);
    this.clearSearch();
    const folderId = typeof folder === 'string' ? folder : folder._id;
    this.router.navigate([`/content/files/${this.institutionId}/${folderId}`], {
      queryParams: { view: this.view },
    });
  }

  openBreadcrumbFolder(folder: Pick<IFolder, '_id' | 'name'>): void {
    if (this.isRootFolder(folder)) {
      this.rootFolderId = folder._id;
      this.openRootFolder();
      return;
    }

    this.openFolder(folder._id);
  }

  openRootFolder(): void {
    this.openFolder(this.rootFolderId || this.rootId || 'root');
  }

  isRootFolder(folder?: Pick<IFolder, '_id' | 'name'>): boolean {
    return !!folder && (folder._id === this.rootFolderId || folder.name === this.institutionId);
  }

  handleToggleSelection(): void {
    this.setAllSelected();
  }

  setAllSelected(): void {
    this.allSelected =
      this.currentFiles.length + this.currentFolders.length ===
      this.selectedFiles.length + this.selectedFolders.length;
  }

  toggleAll(check: boolean): void {
    this.fileList?.toggleAll(check);
  }

  onCancel(): void {
    this.UploadTasks?.forEach((child) => child.cancelUpload());
    this.actionText = this.translate.instant('Upload cancelled');
    this.finalizedFiles = this.uploadFiles.length;
    this.dismissUploadMonitor();
  }

  dismissUploadMonitor(): void {
    this.uploadPanelRef = modifiedSetTimeout(this.resetUpload.bind(this), 3000);
  }

  keepUploadMonitor(): void {
    if (this.uploadPanelRef) {
      clearTimeout(this.uploadPanelRef);
    }
    this.uploadPanelRef = null;
  }

  resetUpload(): void {
    this.uploadPanelRef = null;
    this.isUploading = false;
    this.uploadFiles = [];
    this.finalizedFiles = 0;
    this.actionText = this.translate.instant('Uploading...');
  }

  switchView(initialization = false): void {
    if (this.view === this.views.GRID) {
      this.view = this.views.LIST;
      this.maxNameLength = 40;
      this.filesPerPage = 1000;
    } else {
      this.view = this.views.GRID;
      this.maxNameLength = 20;
      this.filesPerPage = 25;
      // clear selection if a subfolder was selected
      if (!initialization && this.activeFolder?._id.toString() !== this.rootId) {
        this.toggleAll(false);
      }
    }
    // To push a new entry with new view param to browser history.
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { view: this.view },
      queryParamsHandling: 'merge',
    });
  }

  showSnackbar(options: any): void {
    const config = new MatSnackBarConfig();
    config.verticalPosition = 'top';
    config.horizontalPosition = 'center';
    config.duration = options.duration ?? 3000;
    config.data = options;
    config.panelClass = options.error ? 'profiles-snackbar-panel-error' : 'profiles-snackbar-panel';

    this.snackBar.openFromComponent(SnackbarComponent, config);
  }

  allowDrop($event: { preventDefault: () => void }): void {
    $event.preventDefault();
  }

  clearSearch(): void {
    this.filtersService.setSearchValue('');
  }

  toggleFilterPanel(): void {
    this.filterPanelOpen = !this.filterPanelOpen;
  }
}
